import {
  type ControlResultActivityActor,
  type ControlResultActivityComment,
  ControlResultActivityListItem,
} from "@/models/control-result-activity.model";
import type { RichTextDto } from "@/spz-api/model";

/**
 * Factory for creating and transforming ControlResultActivityListItem instances.
 * Handles object creation logic separately from domain logic.
 */
export class ControlResultActivityFactory {
  /**
   * Creates a new activity instance with an updated comment.
   * Maintains immutability for React's change detection.
   */
  static withUpdatedComment(
    activity: ControlResultActivityListItem,
    updatedComment: ControlResultActivityComment,
  ): ControlResultActivityListItem {
    return new ControlResultActivityListItem({
      id: activity.id,
      createdAt: activity.createdAt,
      actor: activity.actor,
      comment: updatedComment,
      attachment: activity.attachment,
      workflowStepChange: activity.workflowStepChange,
      workflowConclusionChange: activity.workflowConclusionChange,
      ownerChange: activity.ownerChange,
      priorityChange: activity.priorityChange,
    });
  }

  /**
   * Creates an optimistic activity item for a new comment.
   * Used for optimistic UI updates before the server responds.
   */
  static createOptimisticComment(
    actor: ControlResultActivityActor,
    content: RichTextDto,
  ): ControlResultActivityListItem {
    const tempId = `temp-${Date.now()}`;
    return new ControlResultActivityListItem({
      id: tempId,
      createdAt: new Date(),
      actor,
      comment: {
        id: tempId,
        // biome-ignore lint/suspicious/noExplicitAny: RichTextDto type comes from API package
        content: content as any,
        hasBeenUpdated: false,
        updatedAt: null,
      },
      attachment: null,
      workflowStepChange: null,
      workflowConclusionChange: null,
      ownerChange: null,
      priorityChange: null,
    });
  }
}
